import { type StationBrand, TypeOfIntegration } from '@centric-os/types';
import { useSiteDetails } from '../..';
import { isEmpty, isNil } from 'lodash-es';
import { useTranslation } from '@centric-os/helpers';
import { SitesLocalePrefix } from '../../../enums';

export default () => {
  // Set station localization
  const { translate } = useTranslation();
  const STATION_PREFIX = SitesLocalePrefix.SITE_STATION;

  // Set site details composable
  const { devices } = useSiteDetails();

  const isThriveEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.thrive?.enabled);
  };

  const isBoostEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.boost?.enabled);
  };

  const isRobotDeliveryEnabled = (station: StationBrand): boolean => {
    const { thrive, boost } = station.meta?.software_integrations ?? {};
    return Boolean(
      (thrive?.enabled && thrive?.robot_delivery) || (boost?.enabled && boost?.robot_delivery),
    );
  };

  const isTacitEnabled = (station: StationBrand, multigroupId: string): boolean => {
    return !station.is?.hidden && multigroupId === import.meta.env.VITE_TACIT_MULTIGROUP;
  };

  const isNextepEnabled = (station: StationBrand): boolean => {
    return Boolean(
      station.meta?.software_integrations?.nextep?.enabled ||
        station.meta?.software_integrations?.nextep?.mobile_app ||
        station.meta?.software_integrations?.nextep?.web,
    );
  };

  const isVolanteEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.volante?.enabled);
  };

  const isAgilysysEnabled = (station: StationBrand): boolean => {
    return Boolean(station.meta?.software_integrations?.agilysys?.enabled);
  };

  const isTime2EatEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.time2eat?.enabled;
  };

  const isMashginEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.mashgin?.enabled;
  };

  const hasCDKdsDevice = (station: StationBrand): boolean =>
    devices.value?.some((device) => device.brands?.includes(station.id));

  const isCentricHubEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.centric_hub?.enabled;
  };

  const isAgilysysDodEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.agilysys_dod?.enabled;
  };

  const isTransactEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.transact?.enabled;
  };

  const isGrubhubEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.grubhub?.enabled;
  };

  const isRystEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.ryst?.enabled;
  };

  const isIllumiaEnabled = (station: StationBrand): boolean => {
    return station.meta?.software_integrations?.illumia?.enabled;
  };

  /**
   * Retrieves the available KDS (Kitchen Display System) types for a given station.
   * This includes a check for specific device-based KDS types like 'CDL'.
   *
   * @param {StationBrand} station - The station object containing metadata for KDS types.
   * @returns {string[]} An array of KDS types available for the station.
   */
  const getIntegrations = (station: StationBrand, multigroupId: string): string[] => {
    const integrationTypes: string[] = [];

    if (isNil(station)) return integrationTypes;

    if (isThriveEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.thrive.name);
    }

    if (isTacitEnabled(station, multigroupId)) {
      integrationTypes.push(TypeOfIntegration.tacit.name);
    }

    if (isBoostEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.boost.name);
    }

    if (isNextepEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.nextep.name);
    }

    if (isVolanteEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.volante.name);
    }

    if (isAgilysysEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.agilysys.name);
    }

    if (isMashginEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.mashgin.name);
    }

    if (isTime2EatEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.time2eat.name);
    }

    if (hasCDKdsDevice(station)) {
      integrationTypes.push(TypeOfIntegration.cdl.name);
    }

    if (isCentricHubEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.centric_hub.name);
    }

    if (isAgilysysDodEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.agilysys_dod.name);
    }

    if (isTransactEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.transact.name);
    }

    if (isGrubhubEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.grubhub.name);
    }

    if (isRystEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.ryst.name);
    }

    if (isIllumiaEnabled(station)) {
      integrationTypes.push(TypeOfIntegration.illumia.name);
    }

    return integrationTypes;
  };

  /**
   * Checks if the user can toggle the "Show in App" option.
   *
   * If an app belongs to the Thrive or Boost multigroup, the user can choose whether a station is displayable in the app.
   *
   * @param {StationBrand} station - The station object containing metadata for KDS types.
   * @returns {boolean} True if the station is visible and part of Boost, Thrive, or Tacit. False otherwise.
   */
  const canToggleShowInApp = (station: StationBrand, multigroupId: string): boolean => {
    const isThriveOrBoostIntegrationEnabled = !!(
      !isEmpty(station.timeslots) ||
      station.is?.scan_and_go_supported ||
      station.is?.frictionless_supported
    );
    return (
      isThriveOrBoostIntegrationEnabled &&
      [import.meta.env.VITE_THRIVE_MULTIGROUP, import.meta.env.VITE_BOOST_MULTIGROUP].includes(
        multigroupId,
      )
    );
  };

  /**
   * Shared rule for the "Show in App" toggle's disabled state, used by both the Menu Hours
   * list and the Station detail integration tile so they stay in sync.
   */
  const isShowInAppToggleDisabled = (flags: {
    isIntegrated?: boolean;
    canEnable?: boolean;
  }): boolean => !(flags.isIntegrated && flags.canEnable);

  return {
    getIntegrations,
    translate,
    isThriveEnabled,
    isBoostEnabled,
    isRobotDeliveryEnabled,
    STATION_PREFIX,
    canToggleShowInApp,
    isShowInAppToggleDisabled,
  };
};
